"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomSlider = void 0;
const react_1 = __importStar(require("react"));
const icons_core_1 = require("@volkswagen-onehub/icons-core");
const components_core_1 = require("@volkswagen-onehub/components-core");
const styled_1 = require("./styled");
const disable_focus_wrapper_1 = require("../disable-focus-wrapper");
const link_button_1 = require("../link-button");
const useResponsive_1 = require("./hooks/useResponsive");
const CustomSlider = ({ buttonsPosition = 'bottom', onActionTrackingTrigger, onInit, onSlideChange, scrollByOne = false, slides = [], slidesToShow = 3.2, slidesToShowMedium = 2.2, slidesToShowMobile = 1.2, slidesToShowTablet = 1.2 }) => {
    const [currentIndex, setCurrentIndex] = (0, react_1.useState)(0);
    const [currentX, setCurrentX] = (0, react_1.useState)(0);
    const [slideWidth, setSlideWidth] = (0, react_1.useState)(0);
    const [startX, setStartX] = (0, react_1.useState)(0);
    const [isDragging, setIsDragging] = (0, react_1.useState)(false);
    const [isTransitioning, setIsTransitioning] = (0, react_1.useState)(false);
    const sliderRef = (0, react_1.useRef)(null);
    const slideRef = (0, react_1.useRef)(null);
    // Validate slides prop
    if (!Array.isArray(slides)) {
        return (react_1.default.createElement(styled_1.ErrorMessageContainer, null,
            react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.headline200, color: components_core_1.TokenTextColor.inherit }, "Slides must be an array of valid React Elements")));
    }
    const totalSlides = slides.length;
    if (totalSlides > 0) {
        for (let i = 0; i < slides.length; i++) {
            if (!react_1.default.isValidElement(slides[i])) {
                return (react_1.default.createElement(styled_1.ErrorMessageContainer, null,
                    react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.headline200, color: components_core_1.TokenTextColor.inherit },
                        "Element ",
                        [i + 1],
                        " in the array is not a valid React element.")));
            }
        }
    }
    // Call onInit when the component mounts
    (0, react_1.useEffect)(() => {
        if (onInit)
            onInit();
    }, [onInit]);
    // Reset current index when slides change
    (0, react_1.useEffect)(() => {
        if (slides.length > 0)
            setCurrentIndex(0);
    }, [
        slides.length,
        slidesToShow,
        slidesToShowMedium,
        slidesToShowMobile,
        slidesToShowTablet
    ]);
    // Slider's responsive behavior
    const { currentSlidesToScroll, currentSlidesToShow, gap, isMobile } = (0, useResponsive_1.useResponsive)(slidesToShow, slidesToShowMedium, slidesToShowMobile, slidesToShowTablet, scrollByOne);
    (0, react_1.useEffect)(() => {
        // Update slideWidth when currentSlidesToShow changes.
        // This effect is triggered whenever the value of currentSlidesToShow changes.
        // It measures the width of the first slide and updates the slideWidth state variable.
        // The conditional ensures that slideRef.current exists before accessing its properties
        if (sliderRef.current && slideRef.current) {
            setSlideWidth(slideRef.current.offsetWidth);
        }
    }, [currentSlidesToShow]);
    (0, react_1.useEffect)(() => {
        // Update slideWidth on window resize.
        // This effect handles window resize events.
        // It measures the width of the first slide and updates the slideWidth state variable.
        // The handleResize function is called immediately to get an initial measurement,
        // and the event listener ensures that slideWidth is updated on every resize.
        const handleResize = () => {
            if (sliderRef.current && slideRef.current) {
                setSlideWidth(slideRef.current.offsetWidth);
            }
        };
        handleResize(); // Initial measurement
        window.addEventListener('resize', handleResize);
        // Cleanup function to remove the event listener when the component unmounts.
        return () => window.removeEventListener('resize', handleResize);
    }, [sliderRef, slideRef]);
    (0, react_1.useEffect)(() => {
        // Observe slider container for resize using ResizeObserver.
        // This effect uses ResizeObserver to efficiently detect changes in the size
        // of the slider container. When the size changes, it updates the slideWidth.
        // It's more efficient than using window resize events because it only triggers
        // when the observed element's size changes.
        const resizeObserver = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry && slideRef.current) {
                setSlideWidth(slideRef.current.offsetWidth);
            }
        });
        const targetElement = sliderRef.current; // Store the ref value to avoid warnings
        if (targetElement)
            resizeObserver.observe(targetElement);
        // Cleanup function to disconnect the observer when the component unmounts.
        return () => {
            if (targetElement)
                resizeObserver.unobserve(targetElement);
        };
    }, [sliderRef, slideRef]);
    // Determine if the last slide should be full width (mobile only)
    const isLastSlideFullWidth = (0, react_1.useCallback)((index) => {
        if (!isMobile)
            return false;
        if (totalSlides === 1)
            return true;
        return (index === totalSlides - 1 &&
            currentIndex >= totalSlides - Math.ceil(currentSlidesToShow));
    }, [isMobile, totalSlides, currentIndex, currentSlidesToShow]);
    // Function to navigate to a specific slide
    const goToSlide = (0, react_1.useCallback)((index, type) => {
        if (totalSlides === 0)
            return;
        const maxIndex = Math.max(0, totalSlides - Math.floor(currentSlidesToShow));
        const newIndex = Math.min(Math.max(0, index), maxIndex);
        if (onSlideChange)
            onSlideChange(index, currentIndex);
        if (onActionTrackingTrigger)
            onActionTrackingTrigger(type, currentIndex > index ? 'previous' : 'next');
        setIsTransitioning(true);
        setCurrentIndex(newIndex);
        setTimeout(() => {
            setIsTransitioning(false);
        }, 400);
    }, [totalSlides, currentSlidesToShow, onSlideChange, currentIndex]);
    // Navigate to the next slide
    const nextSlide = (0, react_1.useCallback)((type) => {
        goToSlide(currentIndex + currentSlidesToScroll, type);
    }, [currentIndex, goToSlide, currentSlidesToScroll]);
    // Navigate to the previous slide
    const prevSlide = (0, react_1.useCallback)((type) => {
        goToSlide(currentIndex - currentSlidesToScroll, type);
    }, [currentIndex, goToSlide, currentSlidesToScroll]);
    // Handle keyboard navigation
    const handleKeyDown = (0, react_1.useCallback)((event) => {
        if (event.key === 'ArrowLeft') {
            prevSlide('click');
        }
        else if (event.key === 'ArrowRight') {
            nextSlide('click');
        }
    }, [prevSlide, nextSlide]);
    // Add keyboard event listener
    (0, react_1.useEffect)(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
    // Handle the start of a drag operation
    const handleDragStart = (0, react_1.useCallback)((clientX) => {
        setStartX(clientX);
        setIsDragging(true);
    }, []);
    // Handle drag movement
    const handleDragMove = (0, react_1.useCallback)((clientX) => {
        if (!isDragging)
            return;
        setCurrentX(clientX - startX);
    }, [isDragging, startX]);
    // Handle the end of a drag operation
    const handleDragEnd = (0, react_1.useCallback)(() => {
        if (!isDragging)
            return;
        setIsDragging(false);
        const threshold = 50; // minimum distance to trigger slide change
        if (currentX < -threshold) {
            nextSlide('swipe');
        }
        else if (currentX > threshold) {
            prevSlide('swipe');
        }
        setCurrentX(0);
    }, [isDragging, currentX, nextSlide, prevSlide]);
    // Set up touch and mouse event listeners for dragging
    (0, react_1.useEffect)(() => {
        const slider = sliderRef.current;
        if (!slider)
            return;
        const handleTouchStart = (e) => handleDragStart(e.touches[0].clientX);
        const handleTouchMove = (e) => handleDragMove(e.touches[0].clientX);
        const handleTouchEnd = () => handleDragEnd();
        const handleMouseDown = (e) => handleDragStart(e.clientX);
        const handleMouseMove = (e) => handleDragMove(e.clientX);
        const handleMouseUp = () => handleDragEnd();
        slider.addEventListener('touchstart', handleTouchStart, { passive: true });
        slider.addEventListener('touchmove', handleTouchMove);
        slider.addEventListener('touchend', handleTouchEnd);
        slider.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            slider.removeEventListener('touchstart', handleTouchStart);
            slider.removeEventListener('touchmove', handleTouchMove);
            slider.removeEventListener('touchend', handleTouchEnd);
            slider.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleDragStart, handleDragMove, handleDragEnd]);
    const renderButtons = () => {
        return (react_1.default.createElement(styled_1.ScrollButtonsWrapper, { className: "scroll-buttons-wrapper", buttonsPosition: buttonsPosition },
            react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.label100 },
                react_1.default.createElement(disable_focus_wrapper_1.DisableFocusWrapper, null,
                    react_1.default.createElement(link_button_1.LinkButton, { className: "nav-button prev", onClick: () => prevSlide('click'), "aria-label": "Previous slide", disabled: currentIndex === 0 },
                        react_1.default.createElement(components_core_1.Container, { gutter: components_core_1.ContainerGutter.static150, verticalAlign: "center" },
                            react_1.default.createElement(icons_core_1.ArrowLeft, { title: "ArrowLeft", ariaLabel: "ArrowLeft" }),
                            react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.label100, color: components_core_1.TokenTextColor.inherit }, "Previous"))))),
            react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.label100 },
                react_1.default.createElement(disable_focus_wrapper_1.DisableFocusWrapper, null,
                    react_1.default.createElement(link_button_1.LinkButton, { className: "nav-button next", onClick: () => nextSlide('click'), "aria-label": "Next slide", disabled: currentIndex >= totalSlides - Math.floor(currentSlidesToShow) },
                        react_1.default.createElement(components_core_1.Container, { gutter: components_core_1.ContainerGutter.static150, verticalAlign: "center" },
                            react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.label100, color: components_core_1.TokenTextColor.inherit }, "Next"),
                            react_1.default.createElement(icons_core_1.ArrowRight, { title: "ArrowRight", ariaLabel: "ArrowRight" })))))));
    };
    // Render empty state if no slides
    if (totalSlides === 0) {
        return (react_1.default.createElement(styled_1.EmptySliderContainer, { clasName: "empty-slider-container" },
            react_1.default.createElement(styled_1.EmptySliderMessage, { className: "empty-slider-message" },
                react_1.default.createElement(components_core_1.Text, { appearance: components_core_1.TokenTextAppearance.headline100, color: components_core_1.TokenTextColor.inherit }, "No slides available"))));
    }
    return (react_1.default.createElement(styled_1.SliderWrapper, { className: "slider", ref: sliderRef, key: totalSlides },
        react_1.default.createElement(styled_1.ButtonsContainer, null, buttonsPosition === 'top' && renderButtons()),
        react_1.default.createElement(styled_1.SliderContainer, { className: "slider-container" },
            react_1.default.createElement(styled_1.SliderTrack, { className: "slider-track", currentIndex: currentIndex, currentSlidesToShow: currentSlidesToShow, gap: gap, isTransitioning: isTransitioning, slideWidth: slideWidth }, slides.map((slide, index) => (react_1.default.createElement(styled_1.SlideComponent, { ref: index === 0 ? slideRef : null, className: "slide", currentSlidesToShow: currentSlidesToShow, gap: gap, isLastSlideFullWidth: isLastSlideFullWidth(index), key: `Slide ${index + 1}` }, slide))))),
        react_1.default.createElement(styled_1.ButtonsContainer, null, buttonsPosition === 'bottom' && renderButtons())));
};
exports.CustomSlider = CustomSlider;
